import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Lightbulb, Check } from 'lucide-react';

const TOUR_STORAGE_KEY = 'syndicappli_tours_completed';

/**
 * GuidedTour - Interactive tour component for onboarding users on specific pages
 * 
 * Usage:
 * <GuidedTour 
 *   tourId="comptabilite"
 *   steps={[
 *     { target: '#exercice-selector', title: 'Exercice comptable', content: 'Sélectionnez l\'exercice...' },
 *     { target: '#budget-section', title: 'Budget', content: 'Gérez votre budget...' }
 *   ]}
 *   onComplete={() => console.log('Tour completed')}
 * />
 */
const GuidedTour = ({ tourId, steps, onComplete, onSkip, autoStart = true }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);

  // Check if tour was already completed
  useEffect(() => {
    const completedTours = JSON.parse(localStorage.getItem(TOUR_STORAGE_KEY) || '{}');
    if (!completedTours[tourId] && autoStart) {
      // Small delay to let the page render
      const timer = setTimeout(() => setIsActive(true), 500);
      return () => clearTimeout(timer);
    }
  }, [tourId, autoStart]);

  // Update target element position
  const updateTargetPosition = useCallback(() => {
    if (!isActive || showWelcome) return;
    
    const step = steps[currentStep];
    if (!step?.target) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(step.target);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      });
      
      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setTargetRect(null);
    }
  }, [isActive, currentStep, steps, showWelcome]);

  useEffect(() => {
    updateTargetPosition();
    window.addEventListener('resize', updateTargetPosition);
    window.addEventListener('scroll', updateTargetPosition);
    return () => {
      window.removeEventListener('resize', updateTargetPosition);
      window.removeEventListener('scroll', updateTargetPosition);
    };
  }, [updateTargetPosition]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    const completedTours = JSON.parse(localStorage.getItem(TOUR_STORAGE_KEY) || '{}');
    completedTours[tourId] = true;
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(completedTours));
    setIsActive(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    const completedTours = JSON.parse(localStorage.getItem(TOUR_STORAGE_KEY) || '{}');
    completedTours[tourId] = true;
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(completedTours));
    setIsActive(false);
    if (onSkip) onSkip();
  };

  const startTour = () => {
    setShowWelcome(false);
  };

  if (!isActive) return null;

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  // Welcome screen before starting the tour
  if (showWelcome) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="guided-tour-welcome">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#00D3A8] to-[#0099CC] p-6 text-white relative">
            <button
              onClick={handleSkip}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
              aria-label="Fermer"
            >
              <X size={20} />
            </button>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Lightbulb size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold">Découvrez cette page</h2>
                <p className="text-white/80 text-sm">Tour guidé interactif</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-slate-600 mb-4">
              Bienvenue ! Ce guide vous présentera les fonctionnalités principales de cette page en <strong>{steps.length} étapes</strong>.
            </p>
            
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-slate-500 mb-2">Vous allez découvrir :</p>
              <ul className="space-y-1.5">
                {steps.slice(0, 4).map((s, i) => (
                  <li key={i} className="flex items-center space-x-2 text-sm text-slate-700">
                    <div className="w-5 h-5 bg-[#00D3A8]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-[#00D3A8]">{i + 1}</span>
                    </div>
                    <span>{s.title}</span>
                  </li>
                ))}
                {steps.length > 4 && (
                  <li className="text-xs text-slate-400 ml-7">
                    + {steps.length - 4} autres fonctionnalités
                  </li>
                )}
              </ul>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={handleSkip}
                className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
              >
                Passer le tour
              </button>
              <button
                onClick={startTour}
                className="flex items-center space-x-2 px-5 py-2.5 bg-[#00D3A8] text-white rounded-xl font-medium hover:bg-[#00B892] transition-colors"
                data-testid="start-tour-btn"
              >
                <span>Commencer</span>
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Overlay with spotlight effect */}
      <div className="fixed inset-0 z-[99] pointer-events-none">
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Spotlight cutout */}
        {targetRect && (
          <div
            className="absolute bg-transparent rounded-lg ring-4 ring-[#00D3A8] ring-offset-4 ring-offset-transparent"
            style={{
              top: targetRect.top - 8,
              left: targetRect.left - 8,
              width: targetRect.width + 16,
              height: targetRect.height + 16,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
            }}
          />
        )}
      </div>

      {/* Tooltip */}
      <div 
        className="fixed z-[100] pointer-events-auto"
        style={{
          top: targetRect ? Math.min(targetRect.top + targetRect.height + 20, window.innerHeight - 250) : '50%',
          left: targetRect ? Math.max(20, Math.min(targetRect.left, window.innerWidth - 420)) : '50%',
          transform: targetRect ? 'none' : 'translate(-50%, -50%)'
        }}
        data-testid="guided-tour-tooltip"
      >
        <div className="bg-white rounded-xl shadow-2xl w-[380px] overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
          {/* Progress bar */}
          <div className="h-1 bg-slate-100">
            <div 
              className="h-full bg-gradient-to-r from-[#00D3A8] to-[#0099CC] transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>

          {/* Header */}
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-[#00D3A8] bg-[#00D3A8]/10 px-2 py-1 rounded-full">
                  Étape {currentStep + 1}/{steps.length}
                </span>
              </div>
              <button
                onClick={handleSkip}
                className="text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Fermer le tour"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            <h3 className="text-lg font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {step?.title}
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              {step?.content}
            </p>
            
            {step?.tip && (
              <div className="mt-3 bg-amber-50 border border-amber-100 rounded-lg p-3">
                <p className="text-xs text-amber-700">
                  <strong>Conseil :</strong> {step.tip}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 pb-5 flex items-center justify-between">
            <div>
              {!isFirstStep && (
                <button
                  onClick={handlePrev}
                  className="flex items-center space-x-1 text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors"
                >
                  <ChevronLeft size={16} />
                  <span>Précédent</span>
                </button>
              )}
            </div>

            <button
              onClick={handleNext}
              className="flex items-center space-x-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg font-medium text-sm hover:bg-[#00B892] transition-colors"
              data-testid="tour-next-btn"
            >
              {isLastStep ? (
                <>
                  <Check size={16} />
                  <span>Terminer</span>
                </>
              ) : (
                <>
                  <span>Suivant</span>
                  <ChevronRight size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Hook to manage tour state
export const useGuidedTour = (tourId) => {
  const [isCompleted, setIsCompleted] = useState(true);

  useEffect(() => {
    const completedTours = JSON.parse(localStorage.getItem(TOUR_STORAGE_KEY) || '{}');
    setIsCompleted(!!completedTours[tourId]);
  }, [tourId]);

  const resetTour = () => {
    const completedTours = JSON.parse(localStorage.getItem(TOUR_STORAGE_KEY) || '{}');
    delete completedTours[tourId];
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(completedTours));
    setIsCompleted(false);
  };

  const completeTour = () => {
    const completedTours = JSON.parse(localStorage.getItem(TOUR_STORAGE_KEY) || '{}');
    completedTours[tourId] = true;
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(completedTours));
    setIsCompleted(true);
  };

  return { isCompleted, resetTour, completeTour };
};

// Pre-defined tour configurations for different pages
export const TOUR_CONFIGS = {
  comptabilite: {
    tourId: 'comptabilite-tour',
    steps: [
      {
        target: '[data-tour="exercice-selector"]',
        title: 'Sélection de l\'exercice',
        content: 'Commencez par sélectionner ou créer un exercice comptable. L\'exercice correspond généralement à une année civile.',
        tip: 'Créez un nouvel exercice avant chaque Assemblée Générale.'
      },
      {
        target: '[data-tour="tabs-navigation"]',
        title: 'Navigation par onglets',
        content: 'Naviguez entre les différentes sections : Budget, Appels de fonds, Factures, Banque, Impayés et Rapports.',
      },
      {
        target: '[data-tour="budget-section"]',
        title: 'Budget prévisionnel',
        content: 'Définissez le budget annuel par catégorie de charges. Ce budget sera voté en AG et servira de base aux appels de fonds.',
        tip: 'Basez-vous sur les dépenses réelles de l\'année précédente.'
      },
      {
        target: '[data-tour="appels-section"]',
        title: 'Appels de fonds',
        content: 'Générez les appels de fonds trimestriels. Chaque coproprietaire recevra sa quote-part selon ses tantièmes.',
      },
      {
        target: '[data-tour="guide-compta"]',
        title: 'Guide comptable',
        content: 'Consultez notre guide pas-à-pas pour une comptabilité conforme aux normes légales françaises.',
        tip: 'Suivez les étapes dans l\'ordre pour ne rien oublier.'
      }
    ]
  },
  assemblees: {
    tourId: 'assemblees-tour',
    steps: [
      {
        target: '[data-tour="ag-list"]',
        title: 'Liste des Assemblées',
        content: 'Visualisez toutes vos Assemblées Générales : à venir, en cours ou passées. Cliquez sur une AG pour accéder à ses détails.',
      },
      {
        target: '[data-tour="new-ag-btn"]',
        title: 'Créer une AG',
        content: 'Créez une nouvelle Assemblée Générale en définissant la date, le lieu et l\'ordre du jour.',
        tip: 'Envoyez les convocations au moins 21 jours avant la date prévue.'
      },
      {
        target: '[data-tour="ag-tabs"]',
        title: 'Onglets de l\'AG',
        content: 'Chaque AG dispose de 6 onglets : Informations, Documents, Résolutions, Salle de réunion, Vote par correspondance et Compte-rendu.',
      },
      {
        target: '[data-tour="resolutions-tab"]',
        title: 'Résolutions',
        content: 'Ajoutez les résolutions à voter. Chaque résolution a un type de majorité (art. 24, 25, 26 ou unanimité).',
        tip: 'Vous pouvez importer des résolutions depuis un fichier Word ou CSV.'
      },
      {
        target: '[data-tour="legal-panel"]',
        title: 'Conformité légale',
        content: 'Le panneau de conformité vous alerte sur les délais légaux et les étapes à ne pas oublier.',
      }
    ]
  },
  visites: {
    tourId: 'visites-tour',
    steps: [
      {
        target: '[data-tour="visites-list"]',
        title: 'Liste des visites',
        content: 'Consultez l\'historique de vos visites d\'immeuble : planifiées, en cours ou terminées.',
      },
      {
        target: '[data-tour="new-visite-btn"]',
        title: 'Planifier une visite',
        content: 'Créez une nouvelle visite en sélectionnant l\'immeuble et la date. Définissez les zones à inspecter.',
        tip: 'Planifiez des visites trimestrielles pour un suivi efficace.'
      },
      {
        target: '[data-tour="visite-zones"]',
        title: 'Zones d\'inspection',
        content: 'Chaque visite est organisée par zones (parties communes, toiture, façades...). Cochez les éléments inspectés.',
      },
      {
        target: '[data-tour="anomalies-section"]',
        title: 'Signalement d\'anomalies',
        content: 'Signalez les anomalies constatées avec photos et niveau d\'urgence. Elles peuvent être converties en incidents.',
      }
    ]
  },
  carnetEntretien: {
    tourId: 'carnet-entretien-tour',
    steps: [
      {
        target: '[data-tour="building-selector"]',
        title: 'Sélection de l\'immeuble',
        content: 'Choisissez l\'immeuble dont vous souhaitez consulter le carnet d\'entretien.',
      },
      {
        target: '[data-tour="fiche-technique"]',
        title: 'Fiche technique',
        content: 'Renseignez les informations techniques de l\'immeuble : structure, équipements, diagnostics obligatoires.',
        tip: 'Cette fiche est obligatoire selon la loi SRU.'
      },
      {
        target: '[data-tour="equipements-tab"]',
        title: 'Équipements',
        content: 'Listez tous les équipements collectifs : ascenseur, chaudière, VMC... avec leurs contrats d\'entretien.',
      },
      {
        target: '[data-tour="travaux-tab"]',
        title: 'Historique des travaux',
        content: 'Conservez l\'historique de tous les travaux réalisés avec les références des entreprises et garanties.',
      },
      {
        target: '[data-tour="export-pdf"]',
        title: 'Export PDF',
        content: 'Exportez le carnet d\'entretien complet en PDF pour l\'archivage ou la transmission aux coproprietaires.',
      }
    ]
  }
};

export default GuidedTour;
